import type { ProductInfo } from "@expert/contentful";
import type { Partner } from "@expert/shared-types";
import type { LocaleCode } from "contentful";
import type { LiteralUnion } from "type-fest";

import type { Address, ChannelKind, Customer } from "./domain";

export const EligibilityStatus = Object.freeze({
    Ineligible: "INELIGIBLE",
    Eligible: "ELIGIBLE",
    Pending: "PENDING_ENROLLMENT",
    Enrolled: "ALREADY_ENROLLED",
    Unknown: "UNKNOWN",
});
export type EligibilityStatus = (typeof EligibilityStatus)[keyof typeof EligibilityStatus];

export const SaleProgress = Object.freeze({
    InProgress: "INPROGESS",
    Success: "SUCCESS",
    Failure: "FAILURE",
    Declined: "DECLINED",
});
export type SaleProgress = (typeof SaleProgress)[keyof typeof SaleProgress];

export const SalesProvider = Object.freeze({
    ASP: "asp",
    EXPERT_SALES: "expert-sales",
    EXPERT_UNIVERSITY: "expert-university",
    NOOP: "noop",
});
export type SalesProvider = (typeof SalesProvider)[keyof typeof SalesProvider];

export const EnrollmentStatus = Object.freeze({
    Success: "SUCCESS",
    Error: "ERROR",
    Failure: "FAILURE",
});
export type EnrollmentStatus = (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];

export interface SalesOpportunity {
    sessionId: string;
    partner: Partner;
}

export type IntegratedSalesSteps =
    | "SuggestedOfferStep"
    | "SendLinkStep"
    | "HelpCustomerFindSubscribeButtonStep"
    | "InlineSaleTrackerStep"
    | "CollectCustomerInfoStep"
    | "WaitingForEnrollmentStep";

export type CommunicationType = LiteralUnion<"SMS" | "EMAIL", string>;

export interface EligibilityMeta {
    mdnLookupResult: {
        hasReachedMaxMdnLookups: boolean;
        previousHashedMdnLookups: string[];
    };
}

export interface EligibleProduct {
    id: string;
    productSku: string;
    status: EligibilityStatus;
    saleProgress: SaleProgress;
    isPrimaryProduct: boolean;
    confirmationCodePattern?: string;
    needsAddress?: string[] | undefined;
    isProductAsurionBilled?: boolean;
    requiresNyDisclosures?: boolean;
    isConfettiShown?: boolean;
    salesProvider: SalesProvider;
    productInfo?: Record<LocaleCode, ProductInfo>;
}

export type SmartLinkMeta = {
    maximumSmartLinksGenerated: boolean;
    /** Only populated by surfaces that read `remainingAttempts` from the send-notification response. */
    remainingAttempts?: number;
};

export interface SaleStatusStyle {
    bgColor: string;
    fontColor: string;
}

export const StatusStyles: Record<string, SaleStatusStyle> = {
    Success: {
        bgColor: "var(--mantine-color-success-5)",
        fontColor: "var(--mantine-color-dark-9)",
    },
    Neutral: {
        bgColor: "var(--mantine-color-dark-2)",
        fontColor: "white",
    },
    Error: {
        bgColor: "var(--mantine-color-red-8)",
        fontColor: "white",
    },
};

export interface EligibilityAttributes {
    isInternationalCaller?: boolean;
    currentPlan?: string;
    chatSourceApp?: string;
    currentSubscriptions?: string[];
    expertLocation?: string;
    isCustomerInProtectedState?: boolean;
    client?: string;
    isVmpSalesEnabled?: boolean;
    isPrecheck?: boolean;
}

export type CustomerInput = Pick<Customer, "firstName" | "lastName" | "email" | "accountNumber">;

export interface EligibilityRequest {
    partner: Partner;
    sessionId: string;
    customerMdn?: string;
    countryCallingCode?: string;
    reservationSid?: string;
    asurionCallId?: string;
    customerInteractionId?: string;
    address?: Address;
    customer?: CustomerInput;
    eligibilityAttributes?: EligibilityAttributes;
    salesChannel?: ChannelKind;
}

export interface Enrollment {
    sessionId: string;
    partner: Partner;
    productOfferId: string;
    confirmationCode?: string;
    productSku: string;
}

export interface Declination {
    sessionId: string;
    partner: Partner;
    productOfferId: string;
    productSku: string;
    reason?: string;
}

export interface EnrollmentResponse {
    enrollmentStatus: EnrollmentStatus;
    productSku: string;
}
